import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Box, Typography, Divider, Button } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { actions as modalActions } from 'redux/modal';
import {
  formatTrainingModuleLabel,
  formatTrainingPartLabel,
} from '../TrainingSessionDetails/helpers/formatTrainingLabels';

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: '500px',
  },
  divider: {
    margin: '20px 0',
  },
  infoLabel: {
    display: 'inline-block',
    marginBottom: '2px',
  },
  activeStatus: {
    color: (theme.palette.success && theme.palette.success.main) || '#2e7d32',
  },
  footer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const formatDate = value => (value ? moment(value).format('MM/DD/YYYY') : '—');

const SessionView = ({ session }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const {
    startDate,
    endDate,
    description = '',
    trainerName,
    trainingModuleName,
    trainingModuleCode,
    trainingPartVersionCode,
    trainingMethod,
    deliveryMethod,
    status,
    stationId,
    stationName,
  } = session;

  const trainingModuleLabel = formatTrainingModuleLabel(trainingModuleCode, trainingModuleName);
  const trainingPartLabel = formatTrainingPartLabel(trainingPartVersionCode, trainingMethod);
  const deliveryMethodLabel = deliveryMethod
    ? t(`trainingSessions.deliveryMethods.${deliveryMethod}`, { defaultValue: deliveryMethod })
    : '—';
  const statusLabel = status ? t(`trainingSessions.statuses.${status}`, { defaultValue: status }) : '—';
  const scopeLabel =
    stationId == null ? t('trainingSessions.scopes.global') : stationName || t('trainingSessions.scopes.local');
  const isElearning = deliveryMethod === 'elearning';
  const fromLabel = isElearning ? t('formLabels.availableFrom') : t('schedule.startDate');
  const toLabel = isElearning ? t('tablesLabels.availableTo') : t('schedule.endDate');

  return (
    <div className={classes.root}>
      <Grid container justify="space-between">
        <Grid item xs={5}>
          <Box mb={2}>
            <span className={classes.infoLabel}>
              {fromLabel}
              {':'}
            </span>
            <Typography variant="body2" color="textSecondary" component="p">
              {formatDate(startDate)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={5}>
          <Box mb={2}>
            <span className={classes.infoLabel}>
              {toLabel}
              {':'}
            </span>
            <Typography variant="body2" color="textSecondary" component="p">
              {formatDate(endDate)}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Divider className={classes.divider} />
      <Grid container direction="column">
        <Grid container item justify="space-between">
          <Grid item xs={5}>
            <Box mb={2}>
              <span className={classes.infoLabel}>
                {t('trainingSessions.trainingModule')}
                {':'}
              </span>
              <Typography variant="body2" color="textSecondary" component="p">
                {trainingModuleLabel || '—'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={5}>
            <Box mb={2}>
              <span className={classes.infoLabel}>
                {t('trainingSessions.deliveryMethod')}
                {':'}
              </span>
              <Typography variant="body2" color="textSecondary" component="p">
                {deliveryMethodLabel}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container item justify="space-between">
          <Grid item xs={5}>
            <Box mb={2}>
              <span className={classes.infoLabel}>
                {t('trainingSessions.trainingPart')}
                {':'}
              </span>
              <Typography variant="body2" color="textSecondary" component="p">
                {trainingPartLabel || '—'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={5}>
            <Box mb={2}>
              <span className={classes.infoLabel}>
                {t('tablesLabels.status')}
                {':'}
              </span>
              <Typography
                variant="body2"
                color="textSecondary"
                component="p"
                className={status === 'active' ? classes.activeStatus : undefined}
              >
                {statusLabel}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid container item justify="space-between">
          <Grid item xs={5}>
            <Box mb={2}>
              <span className={classes.infoLabel}>
                {t('trainingSessions.scope')}
                {':'}
              </span>
              <Typography variant="body2" color="textSecondary" component="p">
                {scopeLabel}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={5}>
            <Box mb={2}>
              <span className={classes.infoLabel}>
                {t('formLabels.trainers')}
                {':'}
              </span>
              <Typography variant="body2" color="textSecondary" component="p">
                {trainerName || '—'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid item>
          <Box mb={2}>
            <span className={classes.infoLabel}>
              {t('tablesLabels.description')}
              {':'}
            </span>
            <Typography variant="body2" color="textSecondary" component="p">
              {description || '—'}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <div className={classes.footer}>
        <Button variant="contained" color="primary" onClick={() => dispatch(modalActions.closeModal())}>
          {t('buttons.close')}
        </Button>
      </div>
    </div>
  );
};

const dateType = PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]);

SessionView.propTypes = {
  session: PropTypes.shape({
    startDate: dateType,
    endDate: dateType,
    description: PropTypes.string,
    trainerName: PropTypes.string,
    trainingModuleName: PropTypes.string,
    trainingModuleCode: PropTypes.string,
    trainingPartVersionCode: PropTypes.string,
    trainingMethod: PropTypes.string,
    deliveryMethod: PropTypes.string,
    status: PropTypes.string,
    stationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stationName: PropTypes.string,
  }).isRequired,
};

export default SessionView;
